/**
 * Foundation Identity — PersonLinkDialog.
 *
 * Dialog inline per completare l'anagrafica di un utente legacy: chiede il
 * Codice Fiscale (obbligatorio) + nome/cognome/data nascita opzionali, poi
 * chiama POST /api/identity/users/{user_id}/link-person.
 *
 * Al completamento, se `onLinked` e' fornito viene chiamato con la Persona
 * appena collegata (utile per navigare al Fascicolo).
 *
 * Rispetta la direttiva "nessuno stato senza uscita": ogni "CF non associato"
 * deve avere un pulsante che apre questo dialog.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IdCard, Loader2, CheckCircle2, AlertCircle, FolderOpen } from "lucide-react";
import { validateFiscalCode, normalizeFiscalCode } from "./validators";
import * as identityApi from "./api";

export default function PersonLinkDialog({
  open, onOpenChange, userId,
  suggestedFirstName = "", suggestedLastName = "",
  onLinked,
}) {
  const navigate = useNavigate();
  const [cf, setCf] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkedPerson, setLinkedPerson] = useState(null);

  useEffect(() => {
    if (open) {
      setCf(""); setBusy(false); setLinkedPerson(null);
      setFirstName(suggestedFirstName || "");
      setLastName(suggestedLastName || "");
      setBirthDate("");
    }
  }, [open, suggestedFirstName, suggestedLastName]);

  const cfValidation = useMemo(() => validateFiscalCode(cf), [cf]);

  const submit = async () => {
    if (!cfValidation.valid) return;
    setBusy(true);
    try {
      const person = await identityApi.linkUserToPerson(userId, {
        fiscal_code: cfValidation.normalized,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        birth_date: birthDate || null,
      });
      setLinkedPerson(person);
      toast.success("Codice Fiscale associato", {
        description: `Persona ${person.first_name} ${person.last_name}`,
      });
      onLinked?.(person);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.detail || JSON.stringify(detail || e?.message || "Errore"));
      toast.error("Impossibile collegare la Persona", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const openDossier = () => {
    if (!linkedPerson) return;
    onOpenChange(false);
    navigate(`/persone/${linkedPerson.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent formDialog data-testid="person-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdCard className="h-5 w-5 text-primary" />
            {linkedPerson ? "Anagrafica associata" : "Completa anagrafica Persona"}
          </DialogTitle>
          <DialogDescription>
            {linkedPerson
              ? "La Persona e' stata collegata all'account. Vuoi aprire il Fascicolo per completare i restanti dati?"
              : "Inserisci il Codice Fiscale della Persona: verra' creata (o collegata) e associata all'account."}
          </DialogDescription>
        </DialogHeader>

        {linkedPerson ? (
          <Alert className="bg-emerald-50 border-emerald-200">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertTitle>{linkedPerson.first_name} {linkedPerson.last_name}</AlertTitle>
            <AlertDescription>
              <span className="font-mono">{linkedPerson.fiscal_code}</span>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Codice Fiscale <span className="text-red-500">*</span></label>
              <Input
                value={cf}
                onChange={(e) => setCf(normalizeFiscalCode(e.target.value))}
                placeholder="RSSMRA80A01H501U"
                maxLength={16}
                className="uppercase font-mono"
                data-testid="person-link-cf"
              />
              {cf && !cfValidation.valid && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {cfValidation.error}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                          data-testid="person-link-first-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Cognome</label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                          data-testid="person-link-last-name" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Data di nascita (facoltativa)</label>
              <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)}
                        data-testid="person-link-birth-date" />
            </div>
            <p className="text-xs text-slate-500">
              I restanti dati (contatti, indirizzo, note) si completano dal Fascicolo Persona.
            </p>
          </div>
        )}

        <DialogFooter>
          {linkedPerson ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Chiudi</Button>
              <Button onClick={openDossier} data-testid="person-link-open-dossier">
                <FolderOpen className="mr-2 h-4 w-4" /> Apri Fascicolo Persona
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
              <Button onClick={submit} disabled={!cfValidation.valid || busy}
                        data-testid="person-link-submit">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Associa Codice Fiscale
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
